// src/components/core/WeatherWidgetExpandable.jsx
// Richer, expandable weather card for the desktop Locality front page's Directory column.
// Brief: Architect — Weather Widget (richer widget + Open-Meteo, national-scale caching), 29 Jul 2026.
//
// Backend wiring (29 Jul 2026): calls the get-weather Edge Function (on-demand fetch + grid-cell
// TTL cache against weather_grid_cache — see supabase/functions/get-weather/index.ts and the
// migration at supabase/migrations/20260729_create_weather_grid_cache.sql). Both are CODE ONLY
// until Michael runs the migration and deploys the function (see Architecture.md § Weather
// System) — until then this shows the "weather unavailable" state, same as any other fetch
// failure. No mock data remains in this file.
//
// Same column width in both states (never a wider modal): collapsed ~135px tall (temp +
// condition + feels-like + a 3-day row), expanded grows downward (H/L, a 4-stat row, a
// next-hours strip, a 5-day list, a coastal line when is_coastal — always false today, no
// coastline import yet — attribution). Drops its own place-name header unconditionally — the
// main app header already names the locality (docs/briefs/2026-09-04-locality-column-headline-
// removal-fix.md; the Directory column's own headline, which this comment used to point to,
// was removed as a redundant second copy of the same name, not corrected).

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle, Cloudy,
  Wind, Droplets, Sunrise, Sunset, ChevronDown, ChevronUp, Navigation
} from 'lucide-react';

// Open-Meteo WMO weather-code mapping (numeric — a different scheme from OpenWeatherMap's icon
// strings, which the original WeatherWidget.jsx still uses for the untouched weather_cache path).
const getWeatherIcon = (code, size = 24) => {
  const iconProps = { size, strokeWidth: 1.5 };
  if (code === 0 || code === 1) return <Sun {...iconProps} />;
  if (code === 2) return <Cloud {...iconProps} />;
  if (code === 3) return <Cloudy {...iconProps} />;
  if (code === 45 || code === 48) return <CloudFog {...iconProps} />;
  if (code >= 51 && code <= 57) return <CloudDrizzle {...iconProps} />;
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain {...iconProps} />;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return <CloudSnow {...iconProps} />;
  if (code >= 95) return <CloudLightning {...iconProps} />;
  return <Sun {...iconProps} />;
};

const WeatherWidgetExpandable = ({ locationCoords = null }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('common');
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);


  useEffect(() => {
    const fetchWeather = async () => {
      if (!locationCoords?.lat || !locationCoords?.lon) {
        setLoading(false);
        setError('No location available');
        return;
      }
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const response = await fetch(`${supabaseUrl}/functions/v1/get-weather`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'apikey': supabaseAnonKey
          },
          body: JSON.stringify({ lat: locationCoords.lat, lon: locationCoords.lon })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || `Weather fetch failed with status ${response.status}`);
        }
        setWeather(data.weather);
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError('Unable to load weather');
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, [locationCoords]);

  const dayLabel = (dateStr) => new Date(dateStr).toLocaleDateString(formattingLocale, { weekday: 'short' });
  const hourLabel = (dateStr) => new Date(dateStr).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit', hour12: false });

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', color: theme.primary, fontSize: '12px' }}>
        {t('weather_loading')}
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', color: '#6c757d', fontSize: '12px' }}>
        {t('weather_unavailable')}
      </div>
    );
  }

  const statBlock = (icon, value, label) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
      <div style={{ color: theme.primary }}>{icon}</div>
      <div style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937' }}>{value}</div>
      <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
    </div>
  );

  const daily = weather.daily || [];
  const hourly = weather.hourly || [];

  return (
    <div
      onClick={() => setExpanded(v => !v)}
      style={{
        width: '100%',
        fontFamily: 'OpenSauceSans, sans-serif',
        background: 'white',
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        padding: '10px 12px',
        cursor: 'pointer',
        boxSizing: 'border-box'
      }}
    >
      {/* Collapsed: current temp + condition + feels-like */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ color: theme.primary, flexShrink: 0 }}>{getWeatherIcon(weather.weather_code, 32)}</div>
        <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: 1, color: theme.primary, flexShrink: 0 }}>
          {Math.round(weather.temperature)}°
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {weather.weather_description}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            {t('weather_feels_like')} {Math.round(weather.feels_like)}°
          </div>
        </div>
      </div>

      {/* Collapsed: 3-day row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eef1f5' }}>
        {daily.slice(0, 3).map((day, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flex: 1 }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>{dayLabel(day.date)}</div>
            <div style={{ color: theme.primary }}>{getWeatherIcon(day.weather_code, 16)}</div>
            <div style={{ fontSize: '10px', color: '#1f2937' }}>
              <span style={{ fontWeight: '700' }}>{Math.round(day.temp_max)}°</span>
              <span style={{ color: '#9ca3af' }}> / {Math.round(day.temp_min)}°</span>
            </div>
          </div>
        ))}
      </div>

      {/* Expand/collapse affordance */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
        marginTop: '6px', fontSize: '11px', fontWeight: '600', color: theme.primary
      }}>
        {expanded ? t('weather_less_detail') : t('weather_more_detail')}
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </div>

      {/* Expanded content — same cached row, no second fetch */}
      {expanded && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eef1f5' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '10px', textAlign: 'center' }}>
            H {Math.round(weather.temp_max)}° · L {Math.round(weather.temp_min)}°
          </div>

          {/* 4-stat row: wind + direction, humidity, UV, rain chance */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '10px' }}>
            {statBlock(
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <Wind size={16} strokeWidth={1.5} />
                <Navigation size={10} strokeWidth={2} style={{ transform: `rotate(${weather.wind_direction}deg)` }} />
              </div>,
              `${Math.round(weather.wind_speed)} km/h`,
              t('weather_wind')
            )}
            {statBlock(<Droplets size={16} strokeWidth={1.5} />, `${weather.humidity}%`, t('weather_humidity'))}
            {statBlock(<Sun size={16} strokeWidth={1.5} />, weather.uv_index, t('weather_uv'))}
            {statBlock(<CloudRain size={16} strokeWidth={1.5} />, `${weather.precipitation_probability}%`, t('weather_rain_chance'))}
          </div>

          {/* Next-hours strip */}
          <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', paddingBottom: '8px', marginBottom: '10px', scrollbarWidth: 'thin' }}>
            {hourly.map((h, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <div style={{ fontSize: '9px', color: '#6b7280' }}>{hourLabel(h.time)}</div>
                <div style={{ color: theme.primary }}>{getWeatherIcon(h.weather_code, 16)}</div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937' }}>{Math.round(h.temperature)}°</div>
              </div>
            ))}
          </div>

          {/* 5-day list */}
          <div style={{ marginBottom: weather.is_coastal ? '10px' : 0 }}>
            {daily.map((day, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 0', fontSize: '11px', color: '#1f2937',
                borderBottom: i < daily.length - 1 ? '1px solid #f3f5f7' : 'none'
              }}>
                <span style={{ flex: 1, textTransform: 'capitalize' }}>{dayLabel(day.date)}</span>
                <div style={{ color: theme.primary }}>{getWeatherIcon(day.weather_code, 14)}</div>
                <span style={{ marginLeft: '8px' }}>
                  <span style={{ fontWeight: '700' }}>{Math.round(day.temp_max)}°</span>
                  <span style={{ color: '#9ca3af' }}> / {Math.round(day.temp_min)}°</span>
                </span>
              </div>
            ))}
          </div>

          {/* Coastal line — only when the cell is coastal (always false today, no coastline
              import yet); entirely absent otherwise, not just empty */}
          {weather.is_coastal && weather.marine && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '10px', color: '#1f2937',
              paddingTop: '8px', borderTop: '1px solid #eef1f5', marginBottom: '10px'
            }}>
              <span>{t('weather_sea_temp')}: {weather.marine.sea_temperature}°</span>
              <span>{t('weather_waves')}: {weather.marine.wave_height}m</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Sunrise size={12} /> {hourLabel(weather.sunrise)}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Sunset size={12} /> {hourLabel(weather.sunset)}
              </span>
            </div>
          )}

          {/* Attribution — required by Open-Meteo's CC BY 4.0 licence */}
          <div style={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center', fontStyle: 'italic' }}>
            {t('weather_attribution')}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherWidgetExpandable;
